import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  ExternalLink,
  Copy,
  Check,
  ShoppingBag,
  Code2,
  Wrench,
  Globe,
  Sparkles,
} from "lucide-react";

type Platform = "shopify" | "shoper" | "woocommerce" | "prestashop" | "wix" | "gtm" | "manual";

interface PlatformInfo {
  id: Platform;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  difficulty: "easy" | "medium" | "hard";
  timeMin: number;
}

const PLATFORMS: PlatformInfo[] = [
  { id: "shopify", label: "Shopify", icon: ShoppingBag, difficulty: "easy", timeMin: 5 },
  { id: "shoper", label: "Shoper", icon: ShoppingBag, difficulty: "easy", timeMin: 5 },
  { id: "woocommerce", label: "WooCommerce", icon: ShoppingBag, difficulty: "easy", timeMin: 10 },
  { id: "prestashop", label: "PrestaShop", icon: ShoppingBag, difficulty: "medium", timeMin: 15 },
  { id: "wix", label: "Wix", icon: Globe, difficulty: "easy", timeMin: 5 },
  { id: "gtm", label: "Google Tag Manager", icon: Wrench, difficulty: "medium", timeMin: 15 },
  { id: "manual", label: "Ręcznie (kod HTML)", icon: Code2, difficulty: "hard", timeMin: 30 },
];

const DIFFICULTY_LABEL: Record<PlatformInfo["difficulty"], { text: string; cls: string }> = {
  easy: { text: "łatwe", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  medium: { text: "średnie", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  hard: { text: "trudne", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
};

export interface PixelInstallGuideModalProps {
  open: boolean;
  onClose: () => void;
}

export default function PixelInstallGuideModal({
  open,
  onClose,
}: PixelInstallGuideModalProps): JSX.Element {
  const [selected, setSelected] = useState<Platform>("shopify");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const copy = (text: string, key: string): void => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0a0b0f] border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-orange-400" />
            Jak zainstalować Meta Pixel na stronie
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 0 — common to all platforms */}
          <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 p-3">
            <div className="text-xs font-semibold text-orange-300 mb-1">
              Najpierw — utwórz Pixel w Meta
            </div>
            <ol className="text-xs text-white/70 space-y-1 list-decimal list-inside">
              <li>
                Wejdź do{" "}
                <a
                  href="https://business.facebook.com/events_manager"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-300 underline"
                >
                  Events Managera
                </a>
              </li>
              <li>
                Kliknij{" "}
                <span className="text-white/90">Połącz źródła danych → Web → Meta Pixel</span>
              </li>
              <li>
                Nadaj nazwę (np. "Sklep główny") → <span className="text-white/90">Utwórz</span>
              </li>
              <li>
                Skopiuj <span className="text-white/90">Pixel ID</span> (16-cyfrowy numer) — będzie
                potrzebny niżej
              </li>
            </ol>
          </div>

          {/* Platform tabs */}
          <div className="flex flex-wrap gap-1.5">
            {PLATFORMS.map((p) => {
              const Icon = p.icon;
              const isActive = selected === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelected(p.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border flex items-center gap-1.5 ${
                    isActive
                      ? "bg-red-500/20 text-white border-red-500/40"
                      : "bg-white/[0.04] text-white/50 border-white/[0.08] hover:text-white/80"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {p.label}
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded text-[9px] uppercase border ${DIFFICULTY_LABEL[p.difficulty].cls}`}
                  >
                    {DIFFICULTY_LABEL[p.difficulty].text} · {p.timeMin}min
                  </span>
                </button>
              );
            })}
          </div>

          {/* Platform-specific instructions */}
          <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-4 text-sm text-white/80 space-y-3">
            {selected === "shopify" && (
              <>
                <h3 className="font-semibold text-white">Shopify — bez kodu, przez panel admina</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W Shopify admin → <span className="text-white">Apps</span> → wyszukaj{" "}
                    <span className="text-white">"Facebook & Instagram"</span> i zainstaluj
                    oficjalną aplikację Meta.
                  </li>
                  <li>Połącz konto Facebook i Business Manager (zgódź się na uprawnienia).</li>
                  <li>
                    W kreatorze app wybierz swój <span className="text-white">Pixel ID</span> z
                    listy lub wklej ręcznie.
                  </li>
                  <li>
                    Włącz <span className="text-white">Conversions API</span> (zalecane — działa
                    nawet z blokadą cookies).
                  </li>
                  <li>
                    Otwórz dowolną stronę produktu i sprawdź w Events Managerze czy event{" "}
                    <span className="text-white">PageView</span> się pojawia (Test Events).
                  </li>
                </ol>
                <p className="text-[11px] text-emerald-300/80">
                  Bonus: Shopify automatycznie wysyła eventy AddToCart, InitiateCheckout, Purchase —
                  nie musisz nic kodować.
                </p>
              </>
            )}

            {selected === "shoper" && (
              <>
                <h3 className="font-semibold text-white">Shoper — przez panel administracyjny</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W panelu Shoper →{" "}
                    <span className="text-white">
                      Dodatki i integracje → Marketing → Facebook Pixel
                    </span>{" "}
                    (lub <span className="text-white">Konfiguracja → Integracje</span> w starszych
                    wersjach).
                  </li>
                  <li>
                    Wklej <span className="text-white">Pixel ID</span> w polu konfiguracyjnym.
                  </li>
                  <li>
                    Zaznacz wszystkie standardowe zdarzenia:{" "}
                    <span className="text-white">
                      PageView, ViewContent, AddToCart, InitiateCheckout, Purchase
                    </span>
                    .
                  </li>
                  <li>Zapisz. Shoper sam doda kod do wszystkich szablonów.</li>
                  <li>Sprawdź w Events Managerze (Test Events) czy eventy strzelają.</li>
                </ol>
                <p className="text-[11px] text-amber-300/80">
                  Uwaga: jeśli używasz własnego szablonu — sprawdź czy hooki Pixela są obecne w
                  pliku <code className="text-white/90">checkout.tpl</code> i{" "}
                  <code className="text-white/90">thankyou.tpl</code>.
                </p>
                <a
                  href="https://www.shoper.pl/help/article/jak-podpiac-pixel-facebooka"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-orange-300 hover:underline"
                >
                  Dokumentacja Shoper <ExternalLink className="w-3 h-3" />
                </a>
              </>
            )}

            {selected === "woocommerce" && (
              <>
                <h3 className="font-semibold text-white">WooCommerce — wtyczka WordPress</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W WP admin → <span className="text-white">Wtyczki → Dodaj nową</span> → wyszukaj{" "}
                    <span className="text-white">"Facebook for WooCommerce"</span> (oficjalna od
                    Meta).
                  </li>
                  <li>Zainstaluj i aktywuj.</li>
                  <li>
                    <span className="text-white">Marketing → Facebook</span> → "Połącz konto" →
                    autoryzuj.
                  </li>
                  <li>
                    Wybierz Pixel z listy (lub wklej Pixel ID ręcznie w ustawieniach wtyczki).
                  </li>
                  <li>
                    Włącz <span className="text-white">Advanced Matching</span> i{" "}
                    <span className="text-white">Conversions API</span>.
                  </li>
                </ol>
                <p className="text-[11px] text-emerald-300/80">
                  Wtyczka automatycznie wysyła Purchase z prawidłową wartością koszyka i product ID.
                </p>
              </>
            )}

            {selected === "prestashop" && (
              <>
                <h3 className="font-semibold text-white">PrestaShop — moduł oficjalny</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W panelu PrestaShop →{" "}
                    <span className="text-white">Modules → Module Manager</span> → wyszukaj{" "}
                    <span className="text-white">"Facebook"</span> (oficjalny moduł Meta Business
                    Extension).
                  </li>
                  <li>
                    Zainstaluj, kliknij <span className="text-white">Configure</span>.
                  </li>
                  <li>Połącz konto FB → wybierz Business Manager → wybierz Pixel.</li>
                  <li>
                    Włącz przesyłanie katalogu produktów (przyda się do reklam dynamicznych — DPA).
                  </li>
                  <li>Sprawdź w Events Managerze że PageView strzela.</li>
                </ol>
              </>
            )}

            {selected === "wix" && (
              <>
                <h3 className="font-semibold text-white">Wix — przez Marketing Tools</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W panelu Wix →{" "}
                    <span className="text-white">Marketing & SEO → Marketing Integrations</span>.
                  </li>
                  <li>
                    Znajdź <span className="text-white">Facebook Pixel</span> → kliknij{" "}
                    <span className="text-white">Connect</span>.
                  </li>
                  <li>
                    Wklej <span className="text-white">Pixel ID</span> i potwierdź.
                  </li>
                  <li>
                    Aktywuj subskrybcję wszystkich standardowych eventów (PageView, ViewContent,
                    AddToCart, Purchase).
                  </li>
                </ol>
                <p className="text-[11px] text-amber-300/80">
                  Wix nie wspiera Conversions API natywnie — Pixel działa tylko po stronie
                  przeglądarki, więc blokady cookies (iOS, Brave) zaniżą wyniki.
                </p>
              </>
            )}

            {selected === "gtm" && (
              <>
                <h3 className="font-semibold text-white">
                  Google Tag Manager — uniwersalny sposób
                </h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    W GTM →{" "}
                    <span className="text-white">Tags → New → Tag Configuration → Custom HTML</span>
                    .
                  </li>
                  <li>Wklej kod base Pixela:</li>
                </ol>
                <div className="relative">
                  <pre className="bg-black/60 border border-white/10 rounded p-3 text-[10px] overflow-x-auto text-white/80">
                    <code>{`<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'TWOJ_PIXEL_ID');
fbq('track', 'PageView');
</script>`}</code>
                  </pre>
                  <button
                    type="button"
                    onClick={() =>
                      copy(
                        `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'TWOJ_PIXEL_ID');
fbq('track', 'PageView');
</script>`,
                        "gtm-base"
                      )
                    }
                    className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80"
                  >
                    {copiedKey === "gtm-base" ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <ol start={3} className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    Podmień <code className="text-white/90">TWOJ_PIXEL_ID</code> na swój 16-cyfrowy
                    ID.
                  </li>
                  <li>
                    Trigger: <span className="text-white">All Pages</span>.
                  </li>
                  <li>Zapisz, opublikuj kontener GTM.</li>
                  <li>
                    Dla eventu Purchase stwórz drugi tag z trigerem{" "}
                    <span className="text-white">Page View → URL contains "/thank-you"</span> (albo
                    dataLayer event po zakupie).
                  </li>
                </ol>
              </>
            )}

            {selected === "manual" && (
              <>
                <h3 className="font-semibold text-white">Ręcznie — wklej kod do {`<head>`}</h3>
                <ol className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    Skopiuj poniższy kod, podmień{" "}
                    <code className="text-white/90">TWOJ_PIXEL_ID</code> na swój ID:
                  </li>
                </ol>
                <div className="relative">
                  <pre className="bg-black/60 border border-white/10 rounded p-3 text-[10px] overflow-x-auto text-white/80">
                    <code>{`<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'TWOJ_PIXEL_ID');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
  src="https://www.facebook.com/tr?id=TWOJ_PIXEL_ID&ev=PageView&noscript=1"/></noscript>`}</code>
                  </pre>
                  <button
                    type="button"
                    onClick={() =>
                      copy(
                        `<!-- Meta Pixel Code -->
<script>
!function(f,b,e,v,n,t,s)
{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
n.callMethod.apply(n,arguments):n.queue.push(arguments)};
if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
n.queue=[];t=b.createElement(e);t.async=!0;
t.src=v;s=b.getElementsByTagName(e)[0];
s.parentNode.insertBefore(t,s)}(window, document,'script',
'https://connect.facebook.net/en_US/fbevents.js');
fbq('init', 'TWOJ_PIXEL_ID');
fbq('track', 'PageView');
</script>
<noscript><img height="1" width="1" style="display:none"
  src="https://www.facebook.com/tr?id=TWOJ_PIXEL_ID&ev=PageView&noscript=1"/></noscript>`,
                        "manual"
                      )
                    }
                    className="absolute top-2 right-2 p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/80"
                  >
                    {copiedKey === "manual" ? (
                      <Check className="w-3.5 h-3.5" />
                    ) : (
                      <Copy className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
                <ol start={2} className="space-y-2 list-decimal list-inside text-xs">
                  <li>
                    Wklej do sekcji <code className="text-white/90">{`<head>`}</code> KAŻDEJ
                    podstrony (najlepiej w globalnym layoucie / template).
                  </li>
                  <li>
                    Na stronie podziękowania po zakupie (thank-you page) dodaj dodatkowo:{" "}
                    <code className="text-white/90">{`fbq('track', 'Purchase', { value: 99.00, currency: 'PLN' });`}</code>
                  </li>
                  <li>
                    Sprawdź w Events Managerze (Test Events) że PageView i Purchase strzelają.
                  </li>
                </ol>
              </>
            )}
          </div>

          {/* Verification step — common */}
          <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
            <div className="text-xs font-semibold text-emerald-300 mb-2">
              Po instalacji — zweryfikuj
            </div>
            <ol className="text-xs text-white/70 space-y-1 list-decimal list-inside">
              <li>
                Zainstaluj rozszerzenie{" "}
                <a
                  href="https://chromewebstore.google.com/detail/meta-pixel-helper/fdgfkebogiimcoedlicjlajpkdmockpc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-300 underline"
                >
                  Meta Pixel Helper
                </a>{" "}
                (Chrome).
              </li>
              <li>
                Otwórz swoją stronę — ikona helpera powinna pokazać 1 Pixel + zielony znacznik.
              </li>
              <li>
                W{" "}
                <a
                  href="https://business.facebook.com/events_manager"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-300 underline"
                >
                  Events Managerze
                </a>{" "}
                → Test Events → wpisz URL strony → klikaj po sklepie i obserwuj eventy w czasie
                rzeczywistym.
              </li>
              <li>
                Po 24h Meta zacznie wykrywać Pixel automatycznie — dopiero wtedy będzie widoczny w
                kreatorze tutaj.
              </li>
            </ol>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              Zamknij
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
